import { Capacitor } from "@capacitor/core";
import { Filesystem, Directory } from "@capacitor/filesystem";
import { Share } from "@capacitor/share";
import {
  BookOpen,
  ClipboardList,
  FileImage,
  FileSpreadsheet,
  FileText,
  FolderKanban,
  HeartHandshake,
  Megaphone,
  Presentation,
  Scale,
  ShieldCheck,
  UsersRound,
} from "lucide-react";
import { getCompanyDocumentUrl } from "@/services/companyDocumentService";

// Mirrors COMPANY_DOCUMENT_CATEGORIES in the backend model.
export const COMPANY_DOCUMENT_CATEGORIES = [
  { value: "hr_policies", label: "HR & Policies", icon: UsersRound },
  { value: "handbook", label: "Handbook", icon: BookOpen },
  { value: "compliance", label: "Compliance", icon: ShieldCheck },
  { value: "benefits", label: "Benefits", icon: HeartHandshake },
  { value: "finance_legal", label: "Finance & Legal", icon: Scale },
  { value: "marketing_sales", label: "Marketing & Sales", icon: Megaphone },
  { value: "projects", label: "Projects", icon: FolderKanban },
  { value: "forms", label: "Forms", icon: ClipboardList },
  { value: "other", label: "Other", icon: FileText },
];

const findCategory = (value) =>
  COMPANY_DOCUMENT_CATEGORIES.find((c) => c.value === value) ||
  COMPANY_DOCUMENT_CATEGORIES[COMPANY_DOCUMENT_CATEGORIES.length - 1];

export const getCategoryLabel = (value) => findCategory(value).label;
export const getCategoryIcon = (value) => findCategory(value).icon;

// ---- "Visible to" ----
export const DEFAULT_AUDIENCE = { mode: "all", departments: [], employee_ids: [] };

export const normalizeAudience = (audience) => ({
  mode: audience?.mode || "all",
  departments: audience?.mode === "departments" ? [...(audience.departments || [])] : [],
  employee_ids: audience?.mode === "selected" ? [...(audience.employee_ids || [])].map(String) : [],
});

export const isSameAudience = (a, b) => {
  const x = normalizeAudience(a);
  const y = normalizeAudience(b);
  const same = (p, q) => p.length === q.length && [...p].sort().join("|") === [...q].sort().join("|");
  return x.mode === y.mode && same(x.departments, y.departments) && same(x.employee_ids, y.employee_ids);
};

// Short label for admin cards.
export const describeAudience = (audience) => {
  const a = normalizeAudience(audience);
  if (a.mode === "departments") return a.departments.join(", ") || "Departments";
  if (a.mode === "selected") {
    const n = a.employee_ids.length;
    return `${n} selected employee${n === 1 ? "" : "s"}`;
  }
  return "All employees";
};

// ---- Acknowledgement ----
// True when this employee still owes one. A login with no employee record gets
// no my_acknowledgement, so it never does.
export const needsAcknowledgement = (doc) =>
  Boolean(doc.acknowledgement_required) &&
  Boolean(doc.my_acknowledgement) &&
  doc.my_acknowledgement.status !== "acknowledged";

export const MAX_FILE_SIZE_MB = 10;

// Client-side pre-check only; the server re-validates extension, MIME and content.
const ALLOWED_EXTENSIONS = [".pdf", ".jpg", ".jpeg", ".png", ".webp", ".docx", ".xlsx", ".pptx"];
export const FILE_INPUT_ACCEPT = ALLOWED_EXTENSIONS.join(",");
export const ALLOWED_TYPES_LABEL = "PDF, JPG, PNG, WEBP, DOCX, XLSX, PPTX";

export function validateSelectedFile(file) {
  if (!file) return "Please choose a file";
  const name = file.name.toLowerCase();
  if (!ALLOWED_EXTENSIONS.some((ext) => name.endsWith(ext))) {
    return `Only ${ALLOWED_TYPES_LABEL} files are allowed`;
  }
  if (file.size === 0) return "The selected file is empty";
  if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
    return `File size must be less than ${MAX_FILE_SIZE_MB}MB`;
  }
  return null;
}

const TYPE_BY_MIME = {
  "application/pdf": { label: "pdf", icon: FileText, preview: "tab" },
  "image/jpeg": { label: "jpg", icon: FileImage, preview: "image" },
  "image/png": { label: "png", icon: FileImage, preview: "image" },
  "image/webp": { label: "webp", icon: FileImage, preview: "image" },
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": {
    label: "docx",
    icon: FileText,
    preview: null,
  },
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": {
    label: "xlsx",
    icon: FileSpreadsheet,
    preview: null,
  },
  "application/vnd.openxmlformats-officedocument.presentationml.presentation": {
    label: "pptx",
    icon: Presentation,
    preview: null,
  },
};

export const getFileType = (mimeType) =>
  TYPE_BY_MIME[mimeType] || { label: "file", icon: FileText, preview: null };

export const isNativePlatform = () => Capacitor.isNativePlatform();

// In-app viewing is web only (same as Employee Documents); on the mobile app
// documents are opened through download + the share sheet. Office files have
// no browser preview, so they are download-only everywhere.
export const canPreview = (doc) => !isNativePlatform() && Boolean(getFileType(doc.mime_type).preview);

export function formatFileSize(bytes) {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

export const includesText = (doc, term) => {
  const q = term.trim().toLowerCase();
  if (!q) return true;
  return [doc.title, doc.description, doc.file_name]
    .filter(Boolean)
    .some((v) => v.toLowerCase().includes(q));
};

const blobToBase64 = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onloadend = () => resolve(String(reader.result).split(",")[1] || "");
    reader.readAsDataURL(blob);
  });

const safeFileName = (name) =>
  String(name || "document").replace(/[<>:"/\\|?*\x00-\x1F]/g, "_");

/**
 * Opens a document for viewing (web only).
 * - PDF: new tab. The tab is opened synchronously, before the link request, so
 *   popup blockers treat it as part of the click.
 * - Images: returns the signed URL for the caller's preview dialog.
 */
export async function openCompanyDocument(doc) {
  const type = getFileType(doc.mime_type);

  if (type.preview === "tab") {
    const tab = window.open("about:blank", "_blank");
    try {
      const { url } = await getCompanyDocumentUrl(doc._id);
      if (tab) {
        tab.opener = null;
        tab.location.href = url;
      } else {
        window.open(url, "_blank", "noopener");
      }
      return null;
    } catch (error) {
      tab?.close();
      throw error;
    }
  }

  const { url } = await getCompanyDocumentUrl(doc._id);
  return url;
}

// Download: web saves through a blob link; native writes to Documents and opens
// the share sheet — same behaviour as Employee Documents.
export async function downloadCompanyDocument(doc) {
  const { url, file_name } = await getCompanyDocumentUrl(doc._id);
  const fileName = safeFileName(file_name || doc.file_name);

  const response = await fetch(url);
  if (!response.ok) throw new Error("Failed to download file");
  const blob = await response.blob();

  if (isNativePlatform()) {
    const base64 = await blobToBase64(blob);
    const written = await Filesystem.writeFile({
      path: fileName,
      data: base64,
      directory: Directory.Documents,
      recursive: true,
    });
    await Share.share({
      title: "Download document",
      text: fileName,
      url: written.uri,
      dialogTitle: "Open / Save document",
    });
    return fileName;
  }

  const objectUrl = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(objectUrl);
  return fileName;
}
